import type { SanityHelper } from './useWwwSanityQuery';
import { MemCacheUtil, BuildMetadata } from '@onshape/www-inertia';
import { hash } from 'ohash';

const locks = new Map<string, unknown>();
let sanityQueriesCount = 0;
let sanityQueriesHitCacheCount = 0;

const cacheFetch = <T>({
  sanity,
  query,
  params = {},
  options = {},
  keyHint = '',
}: {
  sanity: SanityHelper;
  query: string;
  params?: Record<string, unknown>;
  options?: Record<string, unknown>;
  keyHint?: string;
}): Promise<T> => {
  const key = 'sanity-' + keyHint + hash(query + (params ? JSON.stringify(params) : ''));
  if (locks.has(key) && process.env.NUXT_BUILD_MODE !== 'ssr') {
    BuildMetadata.updateSanityQueriesHitCacheCount(++sanityQueriesHitCacheCount);
    return locks.get(key) as Promise<T>;
  }

  const q = MemCacheUtil.getOrGenJsonBlob({
    key,
    ttl: 1000 * 60 * 10,
    generate: async () => {
      const tic = Date.now();
      const q = (await sanity.fetch<T>(query, params, options)) as Promise<JsonBlob>;
      const toc = Date.now();
      const time = toc - tic;
      if (time > 500) {
        BuildMetadata.addSlowQuery({
          query,
          params,
          time,
          keyHint,
        });
      }
      return q;
    },
  });

  BuildMetadata.updateSanityQueriesCount(++sanityQueriesCount);

  locks.set(key, q);
  return locks.get(key) as Promise<T>;
};

export const useCacheFetchSanity = () => {
  return {
    fetch: cacheFetch,
  };
};
